import { isTimeTbd, type MyMatch } from '../../api/matches'

/**
 * One schedule, not three.
 *
 * A user who plays for a club, owns that club and runs a league sees the same
 * fixture from three directions: they are ON the roster, they OWN the team, and
 * their LEAGUE is hosting it. Listing it once per role would be the same match
 * three times with three different headings.
 *
 * So the sources are merged and deduplicated by match id, and each surviving
 * row is labelled with the STRONGEST reason it is on the list. Precedence is
 * playing > team > league, because that is the order in which the match makes a
 * claim on the person: if you are playing, nothing else about your relationship
 * to the fixture matters.
 */

export type UpcomingReason = 'playing' | 'team' | 'league'

/** Lower wins when the same match arrives from several sources. */
const PRECEDENCE: Record<UpcomingReason, number> = {
  playing: 0,
  team: 1,
  league: 2,
}

export const REASON_LABEL: Record<UpcomingReason, string> = {
  playing: 'Playing',
  team: 'Your team',
  league: 'Your league',
}

export interface UpcomingSource {
  reason: UpcomingReason
  matches: MyMatch[]
}

export interface UpcomingEntry {
  match: MyMatch
  reason: UpcomingReason
}

export interface MergeOptions {
  limit?: number
  /** Injected for tests; anything already started is dropped. */
  now?: Date
}

/**
 * Merge every source into one chronological list.
 *
 * Sorting is live first, then by kickoff, then matches whose time is still TBD
 * — NOT by reason — a league fixture tomorrow belongs
 * above one you are playing next week. The reason is a label on the row, not a
 * grouping.
 */
export function mergeUpcoming(
  sources: UpcomingSource[],
  options: MergeOptions = {},
): UpcomingEntry[] {
  const now = options.now ?? new Date()
  const byMatch = new Map<string, UpcomingEntry>()

  for (const source of sources) {
    for (const match of source.matches) {
      if (match.status === 'cancelled' || match.status === 'forfeit') continue
      // `live` has no future kickoff but is very much upcoming, so it is judged
      // on status rather than on the clock.
      // A TBD match has no known kickoff — its `starts_at` is a placeholder
      // that may well sit in the past — so it is never dropped on the clock.
      if (isTimeTbd(match)) {
        if (match.status === 'completed') continue
      } else if (match.status !== 'live' && new Date(match.starts_at).getTime() <= now.getTime()) {
        continue
      }

      const existing = byMatch.get(match.id)
      if (!existing) {
        byMatch.set(match.id, { match, reason: source.reason })
        continue
      }
      if (PRECEDENCE[source.reason] < PRECEDENCE[existing.reason]) {
        // Keep the richer payload where one source carries the hosting org.
        byMatch.set(match.id, {
          match: existing.match.organization ? existing.match : match,
          reason: source.reason,
        })
      }
    }
  }

  const merged = [...byMatch.values()].sort((a, b) => {
    // Live first — it is happening right now regardless of its start time.
    const aLive = a.match.status === 'live' ? 0 : 1
    const bLive = b.match.status === 'live' ? 0 : 1
    if (aLive !== bLive) return aLive - bLive
    // Then every match with a known time, then the TBD ones. Among TBD matches
    // the placeholder still orders them (it keeps rounds in sequence) — it is
    // only never shown.
    const aTbd = isTimeTbd(a.match) ? 1 : 0
    const bTbd = isTimeTbd(b.match) ? 1 : 0
    if (aTbd !== bTbd) return aTbd - bTbd
    const byTime =
      new Date(a.match.starts_at).getTime() - new Date(b.match.starts_at).getTime()
    return byTime || a.match.id.localeCompare(b.match.id)
  })

  return options.limit ? merged.slice(0, options.limit) : merged
}
